import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Users, Activity, Flag, Globe } from "lucide-react";
import { shouldUseFoundationFallback, MOCK_NATION_SCORES } from "@/lib/demo/executiveDemoMock";
import { sponsorFor } from "@/lib/sponsor/sponsorMoments";
import { SponsorMomentBadge } from "@/components/sponsor/SponsorMomentBadge";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";

interface NationScore {
  nation_code: string;
  nation_name: string;
  total_points: number;
  total_predictions: number;
  total_users: number;
  updated_at: string;
}

interface TeamMeta { code: string; name: string; flag: string; primary_hex: string; secondary_hex: string }

const MEDAL = ["from-amber-300 to-yellow-500", "from-slate-200 to-slate-400", "from-orange-400 to-amber-700"];

export function NationWarsLeaderboard({ limit = 20, compact = false }: { limit?: number; compact?: boolean }) {
  const [rows, setRows] = useState<NationScore[]>([]);
  const [teams, setTeams] = useState<Record<string, TeamMeta>>({});
  const [loading, setLoading] = useState(true);

  async function load() {
    const [scoresRes, teamsRes] = await Promise.all([
      supabase
        .from("nation_scores")
        .select("nation_code,nation_name,total_points,total_predictions,total_users,updated_at")
        .order("total_points", { ascending: false })
        .limit(limit),
      supabase.from("worldcup_teams").select("code,name,flag,primary_hex,secondary_hex"),
    ]);
    let dataRows = (scoresRes.data ?? []) as NationScore[];
    if (PREVIEW_MODE) {
      // In PREVIEW the Top Naciones ranking is anchored to the official
      // final (ESP vs ARG). Preserve any real rows that come from Supabase
      // but ensure both finalists lead the board, never Brazil/Mexico
      // residuals from mock data.
      const home: NationScore = {
        nation_code: OFFICIAL_MATCH.homeCode,
        nation_name: OFFICIAL_MATCH.homeName,
        total_points: 1420,
        total_predictions: 312,
        total_users: 184,
        updated_at: new Date().toISOString(),
      };
      const away: NationScore = {
        nation_code: OFFICIAL_MATCH.awayCode,
        nation_name: OFFICIAL_MATCH.awayName,
        total_points: 1310,
        total_predictions: 296,
        total_users: 172,
        updated_at: new Date().toISOString(),
      };
      // PREVIEW DATA CERTIFICATION — Only the two Final contenders exist in
      // Preview. Any residual rows (BRA, MEX, historical seeds) are dropped
      // so Brazil can never surface as leader.
      dataRows = [home, away];
    } else if (dataRows.length === 0 && shouldUseFoundationFallback()) {
      dataRows = MOCK_NATION_SCORES.slice(0, limit) as NationScore[];
    }
    setRows(dataRows);
    const map: Record<string, TeamMeta> = {};
    for (const t of (teamsRes.data ?? []) as TeamMeta[]) map[t.code] = t;
    setTeams(map);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // PR-SCALE-04 — replaced global `nation-wars-lb` postgres_changes
    // subscription with a 30s poll. At Mundial scale every settled prediction
    // fanned a row-change to every leaderboard viewer (10k–500k consumers).
    const id = window.setInterval(() => { void load(); }, 30_000);
    return () => { window.clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const fallbackRows: NationScore[] = useMemo(() => {
    return Object.values(teams)
      .map((t) => ({
        nation_code: t.code,
        nation_name: t.name,
        total_points: 0,
        total_predictions: 0,
        total_users: 0,
        updated_at: new Date().toISOString(),
      }))
      .sort((a, b) => a.nation_name.localeCompare(b.nation_name));
  }, [teams]);

  const displayRows = rows.length > 0 ? rows : fallbackRows;
  const isEmpty = rows.length === 0;

  const totals = useMemo(() => ({
    points: displayRows.reduce((s, r) => s + Number(r.total_points), 0),
    users: displayRows.reduce((s, r) => s + Number(r.total_users), 0),
    preds: displayRows.reduce((s, r) => s + Number(r.total_predictions), 0),
    leader: displayRows[0],
    nations: displayRows.length,
  }), [displayRows]);

  const maxPts = displayRows[0]?.total_points ? Number(displayRows[0].total_points) : 1;

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando ranking…</div>;
  }

  if (isEmpty && compact) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center space-y-2">
          <Trophy className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-base font-medium">Ninguna nación ha sumado puntos todavía.</p>
          <p className="text-xs text-muted-foreground">Participa en las micro-predicciones para abrir el marcador de tu país.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!compact && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={<Globe className="h-4 w-4" />} label={isEmpty ? "Naciones registradas" : "Nación líder"} value={isEmpty ? totals.nations.toLocaleString() : (totals.leader ? `${teams[totals.leader.nation_code]?.flag ?? ""} ${totals.leader.nation_name}` : "—")} />
          <MetricCard icon={<Trophy className="h-4 w-4" />} label="Puntos globales" value={totals.points.toLocaleString()} />
          <MetricCard icon={<Users className="h-4 w-4" />} label={isEmpty ? "Usuarios registrados" : "Usuarios participando"} value={totals.users.toLocaleString()} />
          <MetricCard icon={<Activity className="h-4 w-4" />} label={isEmpty ? "Predicciones resueltas" : "Predicciones"} value={totals.preds.toLocaleString()} />
        </div>
      )}

      <Card className="overflow-hidden border-border/60 bg-gradient-to-b from-background to-muted/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-400" /> Top Naciones del Mundo
          </CardTitle>
          {(() => {
            const sponsor = sponsorFor("rivalry_surge");
            return sponsor ? <SponsorMomentBadge sponsor={sponsor} /> : null;
          })()}
        </CardHeader>
        <CardContent className="space-y-2">
          {displayRows.slice(0, limit).map((r, idx) => {
            const pts = Number(r.total_points);
            const prevPts = idx > 0 ? Number(displayRows[idx - 1].total_points) : pts;
            const delta = idx > 0 ? prevPts - pts : 0;
            const widthPct = Math.max(4, Math.round((pts / maxPts) * 100));
            const team = teams[r.nation_code];
            const medal = idx < 3 ? MEDAL[idx] : "from-primary/60 to-primary/30";
            return (
              <div key={r.nation_code} className="relative rounded-lg border border-border/60 bg-card/40 px-3 py-2.5 overflow-hidden">
                <div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${medal} opacity-20`} style={{ width: `${widthPct}%` }} />
                <div className="relative flex items-center gap-3">
                  <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center font-bold text-sm bg-gradient-to-br ${medal} text-background shadow`}>
                    {idx + 1}
                  </div>
                  <div className="text-2xl leading-none">{team?.flag ?? "🏳️"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold truncate">{r.nation_name}</span>
                      <span className="font-mono text-sm font-bold">{pts.toLocaleString()} XP</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{Number(r.total_users).toLocaleString()}</span>
                      <span className="flex items-center gap-1"><Activity className="h-3 w-3" />{Number(r.total_predictions).toLocaleString()}</span>
                      {idx > 0 && delta > 0 && !isEmpty && (
                        <Badge variant="outline" className="h-4 text-[10px] px-1">-{delta.toLocaleString()} vs #{idx}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
        <div className="text-xl font-bold mt-1 truncate">{value}</div>
      </CardContent>
    </Card>
  );
}